import { Badge, Box, Grid, Text, Title } from "@mantine/core";
import dayjs from "dayjs";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { ApiGetTodayPeriodo } from "../api/periodos";
import {
  ApiGetAllTicketsWithDate,
  ApiGetTotalTicketWithDatesByTipoAndComida
} from "../api/tickets";
import { ApiGetAllUsers, ApiGetTotalAllUsers } from "../api/usuarios";
import BirthdayFireworksCanvas from "../components/BirthdayGlobos/BirthdayFireworksCanvas";
import ReportCollector from "../components/ReportCollector";
import TicketList from "../components/TicketList";
import { socket } from "../config/socket";

const TicketsPage = () => {
  const [tickets, setTickets] = useState([]);
  const [ticketsReport, setTicketsReport] = useState([]);

  const [totales, setTotales] = useState({
    cuadrilla: 0,
    trabajador: 0,
    estudiante: 0,
    proveedor: 0,
    visitante: 0,
    guardia: 0,
    expatriado: 0,
    noIdentificado: 0,
  });

  const [ticketsTotal, setTicketsTotal] = useState(0);

  const [periodo, setPeriodo] = useState(null);
  const [periodoActual, setPeriodoActual] = useState(null);
  const [usuariosnomina, setUsuariosnomina] = useState([]);
  const [timer, setTimer] = useState(null);

  const isOnline = navigator.onLine;

  const [birthday, setBirthday] = useState([]);

  const checkIsBirthday = useCallback((dateString) => {
    if (!dateString) return false;
    const today = moment();
    const birthDate = moment(dateString, "DD/MM/YYYY");
    return (
      birthDate.date() === today.date() && birthDate.month() === today.month()
    );
  }, []);

  // Manejar la lógica de cumpleaños separadamente
  const handleBirthdayEffect = useCallback(
    (user) => {
      if (!user?.fecha_naci || !user?.nombres) return;

      if (checkIsBirthday(user.fecha_naci)) {
        const name = user.nombres;

        // Agregar nombre y programar su remoción
        setBirthday((prev) => [...prev, name]);

        // Limpiar después de 3 segundos
        setTimeout(() => {
          setBirthday((prev) => prev.filter((n) => n !== name));
        }, 3000);
      }
    },
    [checkIsBirthday]
  );

  // Obtener período actual
  const getPeriodoActual = () => {
    if (!periodo?.pre_comidas_periodo?.length) return null;

    const now = dayjs();
    for (const item of periodo.pre_comidas_periodo) {
      const { hora_inicio, hora_fin } = item.pre_comidas;
      const start = dayjs(`${now.format("YYYY-MM-DD")} ${hora_inicio}`);
      const end = dayjs(`${now.format("YYYY-MM-DD")} ${hora_fin}`);
      const endCorrected = end.isBefore(start) ? end.add(1, "day") : end;

      if (now.isAfter(start) && now.isBefore(endCorrected)) {
        return item;
      }
    }
    return null;
  };

  // Manejo de actualizaciones en tiempo real
  useEffect(() => {
    if (!socket.connected) socket.connect();

    const handleTicketUpdate = (item) => {
      if (item.sala === "ticketsEmitidos") {
        let ticket = item.data;
        console.warn("Ticket actualizado", item.data);
        handleBirthdayEffect(ticket.user);
        ticket.sync = true;

        setTickets((prev) => {
          const existingIndex = prev.findIndex((t) => t.uuid4 === ticket.uuid4);

          if (existingIndex === -1) return [ticket, ...prev];

          if (JSON.stringify(prev[existingIndex]) === JSON.stringify(ticket)) {
            return prev;
          }

          const newData = [...prev];
          newData[existingIndex] = ticket;
          return newData;
        });
      } else if (item.sala === "ticketPeriodos") {
        handleGetPeriodo();
      }
      fetchData();
    };

    socket.emit("join:ticket", "ticketsEmitidos");
    socket.emit("join:ticket", "ticketPeriodos");
    socket.on("ticket:updated", handleTicketUpdate);

    return () => {
      socket.off("ticket:updated");
      socket.disconnect();
    };
  }, [handleBirthdayEffect, isOnline]);

  useEffect(() => {
    handleGetPeriodo();
    initUsers();
  }, []);

  const fetchData = async () => {
    await initTickets();
    await initTicketsTotal();
  };
  // Cargar datos iniciales
  useEffect(() => {
    const interval = setInterval(() => {
      fetchData();
    }, 2000);
    fetchData();

    return () => clearInterval(interval);
  }, [periodo]);

  // Actualizar período actual
  useEffect(() => {
    const periodoFind = getPeriodoActual();
    setPeriodoActual(periodoFind);
  }, [timer]);

  // Cargar tickets
  const initTickets = async () => {
    const currentPeriodo = getPeriodoActual();
    const inicio = dayjs().format("DD-MM-YYYY");
    const fin = dayjs().format("DD-MM-YYYY");
    // const resTotal = await ApiGetTotalTicketWithDates(inicio, fin);

    if (currentPeriodo) {
      // const totalPaginas = Math.ceil(resTotal.data / 600);
      let ticketsData = [];

      for (let x = 1; x <= 1; x++) {
        const res = await ApiGetAllTicketsWithDate(inicio, fin, x, 15);
        if (res.sms === "ok")
          ticketsData = [...ticketsData, ...res.data.tickets];
      }

      if (ticketsData.length > 0) {
        ticketsData = ticketsData.filter(
          (data) =>
            data?.pre_comidas?.pre_comida_id ===
            currentPeriodo?.pre_comidas?.pre_comida_id
        );
      }
      setTickets(ticketsData);
    }
  };

  const getTicketsByTipo = async (tipo) => {
    const inicio = dayjs().format("DD-MM-YYYY");
    const fin = dayjs().format("DD-MM-YYYY");
    const resTotal = await ApiGetTotalTicketWithDatesByTipoAndComida(
      inicio,
      fin,
      tipo
    );

    return resTotal.success ? resTotal.data : 0;
  };

  const initTicketsTotal = async () => {
    const totalTickets = await getTicketsByTipo(undefined);
    if (totalTickets === 0) return;
    const cuadrilla = await getTicketsByTipo("cuadrilla");
    const trabajador = await getTicketsByTipo("trabajador");
    const estudiante = await getTicketsByTipo("E");
    const proveedor = await getTicketsByTipo("P");
    const visitante = await getTicketsByTipo("V");
    const guardia = await getTicketsByTipo("G");
    const expatriado = await getTicketsByTipo("X");

    setTicketsTotal(totalTickets);
    setTotales({
      cuadrilla,
      trabajador,
      estudiante,
      proveedor,
      visitante,
      guardia,
      expatriado,
      noIdentificado:
        totalTickets -
        (cuadrilla +
          trabajador +
          estudiante +
          proveedor +
          visitante +
          guardia +
          expatriado),
    });
  };

  // Cargar usuarios
  const initUsers = async () => {
    const res = await ApiGetTotalAllUsers();
    if (res.sms === "ok") {
      const totalPages = Math.ceil(res.data / 200);
      let usuarios = [];

      for (let i = 1; i <= totalPages; i++) {
        const { data } = await ApiGetAllUsers({
          page: i,
          pageSize: 200,
          estado: true,
        });
        usuarios = [...usuarios, ...data];
      }

      setUsuariosnomina(usuarios);
    }
  };

  // Obtener período
  const handleGetPeriodo = async () => {
    const response = await ApiGetTodayPeriodo();
    if (response.sms === "ok") setPeriodo(response.data);
  };

  // Helpers para UI
  const obtenerComida = (id) => {
    return (
      periodo?.pre_comidas_periodo?.find(
        (c) => c.pre_comidas.pre_comida_id === id
      )?.pre_comidas.nombre || "N/A"
    );
  };

  const obtenerUsuario = (idUser) => {
    return (
      usuariosnomina.find(
        (user) => parseInt(user.pre_usuario_id) === parseInt(idUser)
      ) || null
    );
  };

  return (
    <>
      {birthday && birthday.length > 0 && (
        <BirthdayFireworksCanvas names={birthday} />
      )}
      <Box
        sx={{
          height: "100vh",
          background: "linear-gradient(135deg, #1a2a6c, #b21f1f, #fdbb2d)",
          backgroundSize: "400% 400%",
          animation: "gradientBG 15s ease infinite",
          display: "flex",
          flexDirection: "column",
          padding: "2rem",
          overflow: "hidden",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.3)",
            zIndex: 1,
            pointerEvents: "none",
          },
        }}
      >
        {/* <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1rem",
          zIndex: 2,
          color: "white",
        }}
      >
        <Title
          sx={{
            fontSize: "2.5rem",
            fontWeight: 700,
            color: "white",
            textShadow: "0 4px 6px rgba(0,0,0,0.3)",
            letterSpacing: "1px",
          }}
        >
          Sistema de Tickets en Tiempo Real
        </Title>
      </Box> */}

        <Grid
          gutter="xl"
          sx={{
            flex: 1,
            overflow: "hidden",
            zIndex: 2,
            justifyContent: "space-between",
            gap: "1rem",
            padding: "0 1rem",
          }}
        >
          <Grid.Col
            span={7}
            sx={{
              display: "flex",
              flexDirection: "column",
              background: "rgba(255,255,255,0.1)",
              backdropFilter: "blur(15px)",
              borderRadius: "16px",
              boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
              border: "1px solid rgba(255,255,255,0.2)",
              overflow: "hidden",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "scale(1.01)",
                boxShadow: "0 15px 35px rgba(0,0,0,0.15)",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "1rem",
                zIndex: 2,
                color: "white",
                padding: "0 1rem",
              }}
            >
              <DateHourSecondCounter timer={timer} setTimer={setTimer} />
              <Box>
                {periodoActual ? (
                  periodoActual.pre_comidas && (
                    <Title
                      sx={{
                        fontSize: "1.5rem",
                        fontWeight: 500,
                        color: "white",
                        textShadow: "0 2px 4px rgba(0,0,0,0.3)",
                      }}
                    >
                      {periodoActual.pre_comidas.nombre}
                      <Text
                        span
                        sx={{
                          fontSize: "1rem",
                          opacity: 0.8,
                          display: "block",
                        }}
                      >
                        Inicio: {periodoActual.pre_comidas.hora_inicio} - Fin:{" "}
                        {periodoActual.pre_comidas.hora_fin}
                      </Text>
                    </Title>
                  )
                ) : (
                  <Text color="white" opacity={0.7} size="lg">
                    Aun no es hora de comida...
                  </Text>
                )}
              </Box>
            </Box>
            <Box
              sx={{
                background: "linear-gradient(45deg, #4a90e2, #50c878)",
                color: "white",
                padding: "1rem",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Title order={2} sx={{ color: "white", margin: 0 }}>
                Tickets Generados
              </Title>
              <Badge
                color="white"
                variant="filled"
                sx={{
                  background: "rgba(255,255,255,0.2)",
                  color: "white",
                }}
                size="xl"
              >
                <Text weight={500} fz={25}>
                  Total: {ticketsTotal}
                </Text>
              </Badge>
            </Box>
            <Box sx={{ flex: 1, overflow: "auto", padding: "1rem" }}>
              <TicketList
                tickets={tickets}
                obtenerComida={obtenerComida}
                obtenerUsuario={obtenerUsuario}
              />
            </Box>
          </Grid.Col>

          <Grid.Col
            span={4}
            sx={{
              display: "flex",
              flexDirection: "column",
              background: "rgba(255,255,255,0.1)",
              backdropFilter: "blur(15px)",
              borderRadius: "16px",
              boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
              border: "1px solid rgba(255,255,255,0.2)",
              overflow: "hidden",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "scale(1.01)",
                boxShadow: "0 15px 35px rgba(0,0,0,0.15)",
              },
            }}
          >
            <Box
              sx={{
                background: "linear-gradient(45deg, #ff6b6b, #feca57)",
                color: "white",
                padding: "1rem",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Title order={3} sx={{ color: "white", margin: 0 }}>
                Recolector
              </Title>
            </Box>
            <Box sx={{ flex: 1, overflow: "auto", padding: "1rem" }}>
              <ReportCollector totales={totales} totalTickets={ticketsTotal} />
            </Box>
          </Grid.Col>
        </Grid>

        {[...Array(5)].map((_, i) => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              background: "rgba(255,255,255,0.05)",
              borderRadius: "50%",
              animation: `float${i} 10s infinite alternate`,
              zIndex: 1,
              "@keyframes": {
                [`float${i}`]: {
                  "0%": {
                    transform: `translate(${Math.random() * 100}%, ${
                      Math.random() * 100
                    }%) rotate(0deg)`,
                  },
                  "100%": {
                    transform: `translate(${Math.random() * 100}%, ${
                      Math.random() * 100
                    }%) rotate(360deg)`,
                  },
                },
              },
              width: `${Math.random() * 200 + 100}px`,
              height: `${Math.random() * 200 + 100}px`,
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
            }}
          />
        ))}
      </Box>
    </>
  );
};

export default TicketsPage;

const DateHourSecondCounter = ({ setTimer, timer }) => {
  const [date, setDate] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => {
      setTimer(new Date());
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  return (
    <div className="d-flex pt-4 pb-3">
      <Badge
        color="teal"
        variant="filled"
        style={{ fontSize: "1.2rem" }}
        mr={5}
      >
        {dayjs(timer).format("DD-MM-YYYY")}
      </Badge>
      <Badge color="teal" variant="filled" style={{ fontSize: "1.2rem" }}>
        {timer?.getHours()}:{timer?.getMinutes()}:{timer?.getSeconds()}
      </Badge>
    </div>
  );
};
